import { /*userEntryToNumber, */ numberToUserDisplay } from '@ebiz/pretty-num';

const sweepType = {
  decade: 'DEC',
  linear: 'LIN',
  octave: 'OCT',
};

const orderedPulseParams = [
  'lowValue',
  'highValue',
  'delayTime',
  'riseTime',
  'fallTime',
  'pulseWidth',
  'period',
  'cycles',
];
const orderedSineParams = ['offset', 'peakAmplitude', 'frequency', 'delayTime', 'dampingFactor', 'phase', 'cycles'];
const orderedSweepParams = ['sweepType', 'points', 'startFrequency', 'stopFrequency'];
const orderedTransientParams = ['step', 'stopTime', 'startTime', 'maxDelayTime'];

const DefaultPorts = {
  R: { name: 'resistor', portCount: 2 },
  C: { name: 'capacitor', portCount: 2 },
  L: { name: 'inductor', portCount: 2 },
  D: { name: 'diode', portCount: 2 },
  V: { name: 'voltage', portCount: 2 },
  I: { name: 'current', portCount: 2 },
};

export class SpiceNetList {
  constructor(netListTemplate, digits) {
    this._netList = netListTemplate.replace(/\r?\n/g, '\r\n');
    this._digits = digits ? digits : 3;
  }

  get netlist() {
    return this._netList;
  }

  getElementValue(name) {
    let lines = this._makeLines(this._netList);
    let portCount = DefaultPorts[name.toUpperCase()[0]].portCount;
    let line = lines[this._findIndex(lines, name)][0];
    let pieces = line.split(' ');
    let newData = pieces.slice(portCount + 1);
    return newData.join(' ');
  }

  getElementPortCount(name) {
    let lines = this._makeLines(this._netList);
    let length = lines[this._findIndex(lines, name)][0].trim().split(/\s+/).length - 2;
    return length ? length : DefaultPorts[name[0]].portCount;
  }

  getSubcircuitPortCount(name) {
    let lines = this._makeLines(this._netList);
    return lines[this._findIndex(lines, name)][0].trim().split(/\s+/).length - 2;
  }

  getSubcircuitName() {
    let lines = this._makeLines(this._netList);
    let startLine = lines[this._findIndex(lines, '.subckt')];
    return startLine[0].split(/\s+/)[1];
  }

  getSubcircuitNetList(name) {
    let lines = this._makeLines(this._netList);
    let startLine = lines[this._findSubcircuitIndex(lines, '.subckt', name) + 1];
    let endLine = lines[this._findSubcircuitIndex(lines, '.ends', name) - 1];
    let subcircuit = this._netList.substring(startLine[1], endLine[1] + endLine[0].length);
    return new SpiceNetList(subcircuit);
  }

  /* Exported Functions */
  updateTitle(newTitle) {
    let lines = this._makeLines(this._netList);
    let line = lines[0];
    let title = newTitle ? newTitle : '*';
    let value = title.substring(0, 1) == '*' ? title : '*' + title;
    let result = this._netList.substring(0, line[1]) + value + this._netList.substring(line[1] + line[0].length);
    this._netList = result;
    return this;
  }

  updateSubcircuitName(oldName, newName) {
    let lines = this._makeLines(this._netList);
    let result = '';
    let startLine = lines[this._findSubcircuitIndex(lines, '.subckt', oldName)];
    let endLine = lines[this._findSubcircuitIndex(lines, '.ends', oldName)];
    result = this._netList.replace(startLine[0], startLine[0].replace(oldName, newName));
    result = result.replace(endLine[0], endLine[0].replace(oldName, newName));
    this._netList = result;
    return this;
  }

  updateSubcircuitNetList(name, netlist) {
    let lines = this._makeLines(this._netList);
    let startLine = lines[this._findSubcircuitIndex(lines, '.subckt', name) + 1];
    let endLine = lines[this._findSubcircuitIndex(lines, '.ends', name) - 1];
    let subcircuit = this._netList.substring(startLine[1], endLine[1] + endLine[0].length);
    let result = this._netList.replace(subcircuit, netlist.netlist);
    this._netList = result;
    return this;
  }

  updateElementValue(name, value, ports) {
    let lines = this._makeLines(this._netList);
    let oldLine = lines[this._findIndex(lines, name)][0];
    let newLine = this._updateElementValue(name, value, ports);
    let result = this._netList.replace(oldLine, newLine);
    this._netList = result;
    return this;
  }

  updateElementValues(valuesList) {
    let lines = this._makeLines(this._netList);
    let result = '';
    valuesList.forEach((value) => {
      let oldLine = lines[this._findIndex(lines, value.name)][0];
      let newLine = this._updateElementValue(value.name, value.value, value.ports);
      result = this._netList.replace(oldLine, newLine);
      this._netList = result;
    });
    return this;
  }

  updateACAnalysis(parameters) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, '.AC')];
    let pieces = line[0].split(/\s/);
    if (pieces[0].toLowerCase() !== '.ac') {
      throw new Error(`Invalid simulation type in template; expecting .AC, received ${pieces[0]}`);
    }
    let values = pieces.slice(1);
    let sweepTypes = ['DEC', 'LIN', 'OCT'];
    if (values.length - 1 >= this._getMaxIndex(orderedSweepParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedSweepParams, sweepTypes);
      let value = `.AC ${newValues.join(' ')}`;
      let result = this._netList.substring(0, line[1]) + value + this._netList.substring(line[1] + line[0].length);
      this._netList = result;
      return this;
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  updateNoiseAnalysis(parameters) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, '.NOISE')];
    let pieces = line[0].split(/\s/);
    if (pieces[0].toLowerCase() !== '.noise') {
      throw new Error(`Invalid simulation type in template; expecting .NOISE, received ${pieces[0]}`);
    }
    let values = pieces.slice(3);
    let sweepTypes = ['DEC', 'LIN', 'OCT'];
    if (values.length - 1 >= this._getMaxIndex(orderedSweepParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedSweepParams, sweepTypes);
      let value = `.NOISE V(OUT) VNOISE ${newValues.join(' ')}`;
      let result = this._netList.substring(0, line[1]) + value + this._netList.substring(line[1] + line[0].length);
      this._netList = result;
      return this;
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  updateTransientAnalysis(parameters) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, '.TRAN')];
    let pieces = line[0].split(/\s/);
    if (pieces[0].toLowerCase() !== '.tran') {
      throw new Error(`Invalid simulation type in template; expecting .TRAN, received ${pieces[0]}`);
    }
    let values = pieces.slice(1);
    if (values.length - 1 >= this._getMaxIndex(orderedTransientParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedTransientParams);
      let value = `.TRAN ${newValues.join(' ')}`;
      let result = this._netList.substring(0, line[1]) + value + this._netList.substring(line[1] + line[0].length);
      this._netList = result;
      return this;
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  /* Exported Voltage Source Functions */
  updateDCVoltageSource(name, voltage) {
    let result = this._updateDCSource(name, voltage);
    this._netList = result;
    return this;
  }

  updateACVoltageSource(name, voltage, delay) {
    let result = this._updateACSource(name, voltage, delay);
    this._netList = result;
    return this;
  }

  updateSineVoltageSource(name, parameters) {
    let result = this._updateSineSource(name, parameters);
    this._netList = result;
    return this;
  }

  updatePulseVoltageSource(name, parameters) {
    let result = this._updatePulseSource(name, parameters);
    this._netList = result;
    return this;
  }

  /* Exported Current Source Functions */
  updateDCCurrentSource(name, current) {
    let result = this._updateDCSource(name, current);
    this._netList = result;
    return this;
  }

  updateACCurrentSource(name, current, delay) {
    let result = this._updateACSource(name, current, delay);
    this._netList = result;
    return this;
  }

  updateSineCurrentSource(name, parameters) {
    let result = this._updateSineSource(name, parameters);
    this._netList = result;
    return this;
  }

  updatePulseCurrentSource(name, parameters) {
    let result = this._updatePulseSource(name, parameters);
    this._netList = result;
    return this;
  }

  /* Internal Functions */
  _makeLines() {
    let index = 0;
    const lines = [];
    const linePattern = /([^\r\n]*)\r?\n/g;
    const termBuffer = this._netList[this._netList.length - 1] === '\n' ? this._netList : this._netList + '\n';
    let line = null;
    while ((line = linePattern.exec(termBuffer)) !== null) {
      lines.push([line[1], index]);
      index = linePattern.lastIndex;
    }
    return lines;
  }

  _findIndex(lines, value) {
    let result = lines.findIndex((line) => {
      let pieces = line[0].trim().split(/\s/);
      return pieces[0].toLowerCase() == value.toLowerCase();
    });
    return result;
  }

  _findSubcircuitIndex(lines, keyword, name) {
    let result = lines.findIndex((line) => {
      let pieces = line[0].trim().split(/\s/);
      return pieces[0].toLowerCase() == keyword.toLowerCase() && pieces[1].toLowerCase() == name.toLowerCase();
    });
    return result;
  }

  _updateElementValue(name, value, ports) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, name)];
    let subCircuitKeys = ['.subckt', '.ends'];
    let pieces = line[0].split(/\s/);
    let result = '';
    if (subCircuitKeys.includes(pieces[0].toLowerCase())) {
      result = line[0].replace(pieces[1], value);
    } else {
      result = ports
        ? line[0].replace(
            pieces[ports + 1],
            isNaN(value) ? value : numberToUserDisplay(value, 'Spice', 3, { showZeroes: false })
          )
        : line[0].replace(
            pieces[pieces.length - 1],
            isNaN(value) ? value : numberToUserDisplay(value, 'Spice', 3, { showZeroes: false })
          );
    }
    return result;
  }

  _updateDCSource(name, value) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, name)];
    let pieces = line[0].split(/\s/);
    let newValue = numberToUserDisplay(value, 'Spice', this._digits, { showZeroes: false });
    pieces[pieces.length - 1] = newValue;
    let _value = pieces.join(' ');
    let result = this._netList.substring(0, line[1]) + _value + this._netList.substring(line[1] + line[0].length);
    this._netList = result;
    return result;
  }

  _updateACSource(name, value, delay) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, name)];
    let pieces = line[0].split(/\s/);

    let newValue = numberToUserDisplay(value, 'Spice', this._digits, { showZeroes: false });
    let _pieces = `${newValue}`;
    if (typeof delay !== 'undefined') {
      let newDelay = numberToUserDisplay(delay, 'Spice', this._digits, { showZeroes: false });
      _pieces = `${newValue} ${newDelay}`;
    }
    pieces[pieces.length - 1] = _pieces;
    let _value = pieces.join(' ');
    let result = this._netList.substring(0, line[1]) + _value + this._netList.substring(line[1] + line[0].length);
    this._netList = result;
    return result;
  }

  _updateSineSource(name, parameters) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, name)];
    let indexOf = line[0].toLowerCase().indexOf('sine');
    if (indexOf == -1) {
      throw new Error(`Template value not containing SINE keyword`);
    }
    let values = line[0].substring(line[0].indexOf('(') + 1, line[0].indexOf(')')).split(' ');
    if (values.length - 1 >= this._getMaxIndex(orderedSineParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedSineParams);
      let value = `${line[0].substring(0, indexOf)}SINE(${newValues.join(' ')})`;
      let result = this._netList.substring(0, line[1]) + value + this._netList.substring(line[1] + line[0].length);
      this._netList = result;
      return result;
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  _updatePulseSource(name, parameters) {
    let lines = this._makeLines(this._netList);
    let line = lines[this._findIndex(lines, name)];
    let indexOf = line[0].toLowerCase().indexOf('pulse');
    if (indexOf == -1) {
      throw new Error(`Template value not containing PULSE keyword`);
    }
    let values = line[0].substring(line[0].indexOf('(') + 1, line[0].indexOf(')')).split(' ');
    if (values.length - 1 >= this._getMaxIndex(orderedPulseParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedPulseParams);
      let value = `${line[0].substring(0, indexOf)}PULSE(${newValues.join(' ')})`;
      let result = this._netList.substring(0, line[1]) + value + this._netList.substring(line[1] + line[0].length);
      this._netList = result;
      return result;
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  _getMaxIndex(list, parameters) {
    let maxIndex = -1;
    for (let param in parameters) {
      maxIndex = Math.max(
        maxIndex,
        list.findIndex((item) => item == param)
      );
    }
    return maxIndex;
  }

  _getUpdatedValues(values, parameters, orderedList, sweepTypes) {
    let result = values.map((value, index) => {
      let isValid = parameters[orderedList[index]] !== null && typeof parameters[orderedList[index]] !== 'undefined';
      if (orderedList[index] == 'sweepType') {
        return isValid && sweepTypes.includes(sweepType[parameters[orderedList[index]]])
          ? sweepType[parameters[orderedList[index]]]
          : value.toUpperCase();
      } else {
        return isValid
          ? numberToUserDisplay(parameters[orderedList[index]], 'Spice', this._digits, { showZeroes: false })
          : value;
      }
    });
    return result;
  }
}
